import type { LegendServiceType } from '@/features/jobs/legends/legend-service-type'
import type {
  CustomerRatingMethodResource,
  DeadlineConfigResource,
  InfoLineResource,
  LegendContentResource,
  LegendServiceTypeResource,
  RatingConfigResource,
} from '@/features/legends/api/resources/legend'
import {
  mapDataSourcesConfigResourceToConfig,
  mapDataSourcesConfigToResource,
} from '@/features/legends/data-sources/data-source-mappers'
import type { LegacyIdFixer } from '@/features/legends/editor/legacy-ids'
import {
  mapRuleResourcesToRules,
  mapRulesToResources,
} from '@/features/legends/filters/rule-mappers'
import type {
  CustomerRatingMethod,
  DeadlineConfig,
  InfoLine,
  LegendOptions,
  RatingConfig,
} from './legend-options'

/**
 * The fields of a legend's content that are its options.
 */
export type LegendOptionsResource = Pick<
  LegendContentResource,
  | 'name'
  | 'description'
  | 'image_url'
  | 'service_type'
  | 'sendNotificationsOnSubmit'
  | 'delayNotification'
  | 'view_order'
  | 'skipBilling'
  | 'alwaysEnableVoiceCall'
  | 'message_config'
  | 'ratingConfig'
  | 'time_config'
  | 'targetRoles'
  | 'deadline_config'
  | 'title_config'
  | 'txf_config'
  | 'pending_config'
  | 'tax_plan_config'
  | 'personal_organizer_config'
  | 'specs_config'
  | 'staleness'
  | 'purge'
  | 'multi_job_flow_config'
  | 'prep_and_review_config'
  | 'data_sources'
  | 'allowProSelection'
  | 'showAvailabilityCount'
  | 'excludeLegacyYears'
  | 'excludeLegacyStates'
>

/**
 * Maps the options of a legend's content, fixing references to legacy
 * question IDs. Fields the builder doesn't edit, like the payout config, are
 * left out.
 *
 * @param resource
 * @param ids
 */
export function mapLegendContentResourceToOptions(
  resource: LegendContentResource,
  ids: LegacyIdFixer,
): LegendOptions {
  const {
    message_config: message,
    time_config: time,
    txf_config: txf,
    pending_config: pending,
    tax_plan_config: taxPlan,
    personal_organizer_config: personalOrganizer,
    staleness,
    purge,
    multi_job_flow_config: multiJobFlow,
    prep_and_review_config: prepAndReview,
  } = resource
  return {
    name: resource.name,
    description: resource.description,
    imageUrl: resource.image_url,
    serviceType: resource.service_type
      ? mapServiceTypeResourceToServiceType(resource.service_type)
      : undefined,
    sendNotificationsOnSubmit: resource.sendNotificationsOnSubmit,
    delayNotification: resource.delayNotification,
    viewOrder: resource.view_order,
    skipBilling: resource.skipBilling,
    alwaysEnableVoiceCall: resource.alwaysEnableVoiceCall,
    messageConfig: message && {
      welcomeMessage: message.welcome_message && {
        enabled: message.welcome_message.enabled,
        text: message.welcome_message.text,
      },
    },
    ratingConfig:
      resource.ratingConfig && mapRatingConfigResource(resource.ratingConfig),
    timeConfig: time && { timeZone: time.time_zone },
    targetRoles: resource.targetRoles,
    deadlineConfig:
      resource.deadline_config &&
      mapDeadlineConfigResource(resource.deadline_config),
    titleConfig: resource.title_config && { expr: resource.title_config.expr },
    txfConfig: txf && {
      integrationEnabledExpr: txf.integration_enabled_expr,
      showQuestionnaire: txf.show_questionnaire,
    },
    pendingConfig: pending && {
      enabledExpr: pending.enabled_expr,
      advanceDateExpr: pending.advance_date_expr,
    },
    taxPlanConfig: taxPlan && {
      integrationEnabledExpr: taxPlan.integration_enabled_expr,
    },
    personalOrganizerConfig: personalOrganizer && {
      enabledExpr: personalOrganizer.enabled_expr,
    },
    specsConfig: resource.specs_config && { expr: resource.specs_config.expr },
    staleness: staleness && {
      enabled: staleness.enabled,
      recurring: staleness.recurring,
      staleAfter: staleness.stale_after,
    },
    purge: purge && {
      enabled: purge.enabled,
      purgeAfter: purge.purge_after,
      documents: purge.documents && { enabled: purge.documents.enabled },
    },
    multiJobFlowConfig: multiJobFlow && {
      enabled: multiJobFlow.enabled,
      questionId: ids.resolve(multiJobFlow.question_id),
      triggerAnswer: multiJobFlow.trigger_answer,
      finalAnswer: multiJobFlow.final_answer,
      previousPro: multiJobFlow.previous_pro,
    },
    prepAndReviewConfig: prepAndReview && {
      enabled: prepAndReview.enabled,
      previousPro: prepAndReview.previous_pro,
      prepServiceItemId: prepAndReview.prep_service_item_id,
      reviewServiceItemId: prepAndReview.review_service_item_id,
      prepAndReviewServiceItemId: prepAndReview.prep_and_review_service_item_id,
    },
    dataSources:
      resource.data_sources &&
      mapDataSourcesConfigResourceToConfig(resource.data_sources),
    allowProSelection: resource.allowProSelection,
    showAvailabilityCount: resource.showAvailabilityCount,
    excludeLegacyYears: resource.excludeLegacyYears,
    excludeLegacyStates: resource.excludeLegacyStates,
  }
}

/**
 * Maps the options to the fields of a legend's content.
 *
 * @param options
 */
export function mapLegendOptionsToContentResource(
  options: LegendOptions,
): LegendOptionsResource {
  const {
    messageConfig: message,
    timeConfig: time,
    txfConfig: txf,
    pendingConfig: pending,
    taxPlanConfig: taxPlan,
    personalOrganizerConfig: personalOrganizer,
    staleness,
    purge,
    multiJobFlowConfig: multiJobFlow,
    prepAndReviewConfig: prepAndReview,
  } = options
  return {
    name: options.name,
    description: options.description,
    image_url: options.imageUrl,
    service_type: options.serviceType
      ? mapServiceTypeToResource(options.serviceType)
      : undefined,
    sendNotificationsOnSubmit: options.sendNotificationsOnSubmit,
    delayNotification: options.delayNotification,
    view_order: options.viewOrder,
    skipBilling: options.skipBilling,
    alwaysEnableVoiceCall: options.alwaysEnableVoiceCall,
    message_config: message && {
      welcome_message: message.welcomeMessage && {
        enabled: message.welcomeMessage.enabled,
        text: message.welcomeMessage.text,
      },
    },
    ratingConfig:
      options.ratingConfig && mapRatingConfigToResource(options.ratingConfig),
    time_config: time && { time_zone: time.timeZone },
    targetRoles: options.targetRoles,
    deadline_config:
      options.deadlineConfig &&
      mapDeadlineConfigToResource(options.deadlineConfig),
    title_config: options.titleConfig && { expr: options.titleConfig.expr },
    txf_config: txf && {
      integration_enabled_expr: txf.integrationEnabledExpr,
      show_questionnaire: txf.showQuestionnaire,
    },
    pending_config: pending && {
      enabled_expr: pending.enabledExpr,
      advance_date_expr: pending.advanceDateExpr,
    },
    tax_plan_config: taxPlan && {
      integration_enabled_expr: taxPlan.integrationEnabledExpr,
    },
    personal_organizer_config: personalOrganizer && {
      enabled_expr: personalOrganizer.enabledExpr,
    },
    specs_config: options.specsConfig && { expr: options.specsConfig.expr },
    staleness: staleness && {
      enabled: staleness.enabled,
      recurring: staleness.recurring,
      stale_after: staleness.staleAfter,
    },
    purge: purge && {
      enabled: purge.enabled,
      purge_after: purge.purgeAfter,
      documents: purge.documents && { enabled: purge.documents.enabled },
    },
    multi_job_flow_config: multiJobFlow && {
      enabled: multiJobFlow.enabled,
      question_id: multiJobFlow.questionId,
      trigger_answer: multiJobFlow.triggerAnswer,
      final_answer: multiJobFlow.finalAnswer,
      previous_pro: multiJobFlow.previousPro,
    },
    prep_and_review_config: prepAndReview && {
      enabled: prepAndReview.enabled,
      previous_pro: prepAndReview.previousPro,
      prep_service_item_id: prepAndReview.prepServiceItemId,
      review_service_item_id: prepAndReview.reviewServiceItemId,
      prep_and_review_service_item_id: prepAndReview.prepAndReviewServiceItemId,
    },
    data_sources:
      options.dataSources &&
      mapDataSourcesConfigToResource(options.dataSources),
    allowProSelection: options.allowProSelection,
    showAvailabilityCount: options.showAvailabilityCount,
    excludeLegacyYears: options.excludeLegacyYears,
    excludeLegacyStates: options.excludeLegacyStates,
  }
}

/**
 * Maps the legend's info lines, fixing references to legacy question IDs.
 *
 * @param resources
 * @param ids
 */
export function mapInfoLineResourcesToInfoLines(
  resources: InfoLineResource[],
  ids: LegacyIdFixer,
): InfoLine[] {
  return resources.map((resource) => ({
    title: resource.title,
    content: resource.content,
    designee: resource.designee,
    triggerWhen: resource.triggerWhen
      ? mapRuleResourcesToRules(resource.triggerWhen, ids)
      : undefined,
  }))
}

/**
 * Maps the legend's info lines to the API's.
 *
 * @param lines
 */
export function mapInfoLinesToResources(lines: InfoLine[]): InfoLineResource[] {
  return lines.map((line) => ({
    title: line.title,
    content: line.content,
    designee: line.designee,
    triggerWhen: line.triggerWhen && mapRulesToResources(line.triggerWhen),
  }))
}

/**
 * Maps a legend's service type.
 *
 * @param serviceType
 */
export function mapServiceTypeResourceToServiceType(
  serviceType: LegendServiceTypeResource,
): LegendServiceType {
  switch (serviceType) {
    case 'BOOKKEEPING':
      return 'Bookkeeping'
    case 'CONSULTATION':
      return 'Consultation'
    case 'MISC':
      return 'Misc'
    case 'TAX':
      return 'Tax'
  }
}

/**
 * Maps a service type to the API's.
 *
 * @param serviceType
 */
export function mapServiceTypeToResource(
  serviceType: LegendServiceType,
): LegendServiceTypeResource {
  switch (serviceType) {
    case 'Bookkeeping':
      return 'BOOKKEEPING'
    case 'Consultation':
      return 'CONSULTATION'
    case 'Misc':
      return 'MISC'
    case 'Tax':
      return 'TAX'
  }
}

function mapRatingConfigResource(resource: RatingConfigResource): RatingConfig {
  return {
    proRatingEnabled: resource.proRatingEnabled,
    platformRatingEnabled: resource.platformRatingEnabled,
    customerRatingMethod:
      resource.customerRatingMethod &&
      mapCustomerRatingMethodResource(resource.customerRatingMethod),
  }
}

function mapRatingConfigToResource(config: RatingConfig): RatingConfigResource {
  return {
    proRatingEnabled: config.proRatingEnabled,
    platformRatingEnabled: config.platformRatingEnabled,
    customerRatingMethod:
      config.customerRatingMethod &&
      mapCustomerRatingMethodToResource(config.customerRatingMethod),
  }
}

function mapCustomerRatingMethodResource(
  method: CustomerRatingMethodResource,
): CustomerRatingMethod {
  switch (method) {
    case 'stars':
      return 'Stars'
    case 'thumbs':
      return 'Thumbs'
  }
}

function mapCustomerRatingMethodToResource(
  method: CustomerRatingMethod,
): CustomerRatingMethodResource {
  switch (method) {
    case 'Stars':
      return 'stars'
    case 'Thumbs':
      return 'thumbs'
  }
}

function mapDeadlineConfigResource(
  resource: DeadlineConfigResource,
): DeadlineConfig {
  return {
    expr: resource.expr,
    reminder: resource.reminder && {
      enabled: resource.reminder.enabled,
      daysBefore: resource.reminder.days_before,
    },
  }
}

function mapDeadlineConfigToResource(
  config: DeadlineConfig,
): DeadlineConfigResource {
  return {
    expr: config.expr,
    reminder: config.reminder && {
      enabled: config.reminder.enabled,
      days_before: config.reminder.daysBefore,
    },
  }
}
